import React from 'react';
import { Loader2, MapPin, Package, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

export type CarrierTrackingEvent = {
  id: string;
  occurredAt: string;
  carrierStatusCode?: string | null;
  normalizedStatus?: string | null;
  description?: string | null;
  location?: string | null;
};

export type CustomerCarrierShipment = {
  id: string;
  provider: string;
  fulfillmentMethod?: string | null;
  status: string;
  serviceName?: string | null;
  masterTrackingNumber?: string | null;
  trackingNumbers?: string[];
  deliveredAt?: string | null;
  lastTrackedAt?: string | null;
  trackingEvents?: CarrierTrackingEvent[];
};

type Props = {
  shipments: CustomerCarrierShipment[];
  onRefresh?: () => void | Promise<void>;
  refreshing?: boolean;
  compact?: boolean;
};

function carrierLabel(provider: string): string {
  if (provider === 'FEDEX') return 'FedEx';
  if (provider === 'UPS') return 'UPS';
  return provider;
}

function externalTrackingUrl(
  provider: string,
  trackingNumber: string,
): string | null {
  if (provider === 'FEDEX') {
    return `https://www.fedex.com/fedextrack/?trknbr=${encodeURIComponent(trackingNumber)}`;
  }
  if (provider === 'UPS') {
    return `https://www.ups.com/track?tracknum=${encodeURIComponent(trackingNumber)}`;
  }
  return null;
}

function shipmentStatusLabel(status: string, t: TFunction): string {
  return t(`account.carrier_status_${status}`, {
    defaultValue: status.replace(/_/g, ' '),
  });
}

export default function CarrierTrackingPanel({
  shipments,
  onRefresh,
  refreshing = false,
  compact = false,
}: Props) {
  const { t } = useTranslation();
  if (!shipments.length) return null;

  return (
    <div
      className={[
        'rounded-xl border border-sky-200 bg-gradient-to-br from-sky-50/80 via-white to-white',
        compact ? 'p-3' : 'p-4',
      ].join(' ')}
    >
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-sky-800">
            <Package className="h-3.5 w-3.5" aria-hidden />
            {t('account.carrier_tracking_title', { defaultValue: 'Shipment tracking' })}
          </div>
          <p className="mt-1 text-xs text-slate-600">
            {t('account.carrier_tracking_subtitle', {
              defaultValue: 'Live scan updates from your carrier.',
            })}
          </p>
        </div>
        {onRefresh ? (
          <button
            type="button"
            onClick={() => void onRefresh()}
            disabled={refreshing}
            className="inline-flex items-center gap-1.5 rounded-full border border-sky-300 bg-white px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-sky-800 hover:border-sky-400 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {refreshing ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
            ) : (
              <RefreshCw className="h-3.5 w-3.5" aria-hidden />
            )}
            {refreshing
              ? t('account.carrier_tracking_refreshing', { defaultValue: 'Updating…' })
              : t('account.carrier_tracking_refresh', { defaultValue: 'Refresh' })}
          </button>
        ) : null}
      </div>

      <div className="space-y-4">
        {shipments.map((shipment) => {
          const trackingNumber =
            shipment.masterTrackingNumber ||
            shipment.trackingNumbers?.[0] ||
            null;
          const externalUrl =
            trackingNumber && shipment.provider
              ? externalTrackingUrl(shipment.provider, trackingNumber)
              : null;
          const events = shipment.trackingEvents || [];

          return (
            <div
              key={shipment.id}
              className="rounded-lg border border-slate-200 bg-white/90 p-3 shadow-sm"
            >
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-sky-100 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-sky-900">
                  {carrierLabel(shipment.provider)}
                </span>
                <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-700">
                  {shipmentStatusLabel(shipment.status, t)}
                </span>
                {shipment.serviceName ? (
                  <span className="text-xs text-slate-600">{shipment.serviceName}</span>
                ) : null}
              </div>

              {trackingNumber ? (
                <div className="mt-2 text-sm text-slate-800">
                  <span className="font-semibold">
                    {t('account.carrier_tracking_number', { defaultValue: 'Tracking #' })}{' '}
                  </span>
                  <span className="font-mono">{trackingNumber}</span>
                  {externalUrl ? (
                    <a
                      href={externalUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="ml-2 text-xs text-sky-700 underline"
                    >
                      {t('account.carrier_tracking_open_carrier', {
                        defaultValue: 'Open on carrier site',
                      })}
                    </a>
                  ) : null}
                </div>
              ) : null}

              {shipment.lastTrackedAt ? (
                <p className="mt-1 text-[11px] text-slate-500">
                  {t('account.carrier_tracking_last_sync', {
                    defaultValue: 'Last updated {{time}}',
                    time: new Date(shipment.lastTrackedAt).toLocaleString(),
                  })}
                </p>
              ) : null}

              {events.length > 0 ? (
                <ol className="mt-3 space-y-0 border-l-2 border-sky-200 pl-4">
                  {events.map((evt) => (
                    <li key={evt.id} className="relative pb-3 last:pb-0">
                      <span
                        className="absolute -left-[1.3rem] top-1 h-2.5 w-2.5 rounded-full border-2 border-white bg-sky-500"
                        aria-hidden
                      />
                      <div className="text-xs font-semibold text-slate-900">
                        {evt.description ||
                          evt.normalizedStatus ||
                          evt.carrierStatusCode ||
                          t('account.carrier_tracking_event_unknown', {
                            defaultValue: 'Scan update',
                          })}
                      </div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-slate-600">
                        <span>{new Date(evt.occurredAt).toLocaleString()}</span>
                        {evt.location ? (
                          <span className="inline-flex items-center gap-0.5">
                            <MapPin className="h-3 w-3" aria-hidden />
                            {evt.location}
                          </span>
                        ) : null}
                      </div>
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="mt-3 text-xs text-slate-600">
                  {t('account.carrier_tracking_no_events', {
                    defaultValue:
                      'No scan events yet. Tracking updates appear here after the carrier scans your package.',
                  })}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
